import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { useTerritory } from '../contexts/TerritoryContext';
import { useTheme } from '../contexts/ThemeContext';
import { SEO } from '../components/SEO';
import { EventCard } from '../components/EventCard';
import { TerritorySelector } from '../components/TerritorySelector';
import { Footer } from '../components/Footer';
import { SeoLinks } from '../components/seo/SeoLinks';
import CategoryRail from '../components/CategoryRail';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, X, Calendar as CalendarIcon, 
  SlidersHorizontal, ChevronLeft, ChevronRight 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Explore = () => {
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const { translateText } = useTranslate();
  const { selectedCommune, selectedRegion } = useTerritory();
  const { isDark } = useTheme();

  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [categoryId, setCategoryId] = useState(searchParams.get('category') || '');
  const [dateFilter, setDateFilter] = useState(searchParams.get('date_filter') || '');
  const [isFree, setIsFree] = useState(null);
  const [audience, setAudience] = useState('');

  const perPage = 12;

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch events when filters change
  useEffect(() => {
    fetchEvents();
  }, [selectedCommune, selectedRegion, categoryId, dateFilter, isFree, audience, page]);

  // Scroll to top when page number changes (pagination navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  // Debounced refetch when search text changes (so clearing the input also refetches)
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchEvents();
    }, 350);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Filter by commune if selected, otherwise by region
      if (selectedCommune) {
        params.append('commune_id', selectedCommune.id);
      } else if (selectedRegion) {
        params.append('region_id', selectedRegion.id);
      }
      
      if (categoryId) params.append('category_id', categoryId);
      if (dateFilter) params.append('date_filter', dateFilter);
      if (isFree !== null) params.append('is_free', isFree.toString());
      if (audience) params.append('target_audience', audience);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());

      const response = await axios.get(`${API_URL}/api/events?${params}`);
      setEvents(response.data.events);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCommune, selectedRegion, categoryId, dateFilter, isFree, audience, searchTerm, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEvents();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryId('');
    setDateFilter('');
    setIsFree(null);
    setAudience('');
    setPage(1);
  };

  const handleTerritoryChange = () => {
    setPage(1);
  };

  const totalPages = Math.ceil(total / perPage);

  const dateFilters = [
    { value: 'today', label: t('today') },
    { value: 'tomorrow', label: t('tomorrow') },
    { value: 'weekend', label: t('weekend') },
    { value: 'upcoming', label: 'Próximamente' },
  ];

  const audiences = [
    { value: 'kids', label: '👶 ' + t('kids') },
    { value: 'adults', label: '👤 ' + t('adults') },
    { value: 'family', label: '👨‍👩‍👧 ' + t('family') },
  ];

  const activeFiltersCount = [categoryId, dateFilter, isFree !== null, audience].filter(Boolean).length;

  return (
    <div className={`min-h-screen ${isDark ? 'night-page' : 'bg-[#F8FAF9]'}`} data-testid="explore-page">
      <SEO
        title="Explorar Panoramas y Eventos"
        description="Busca y filtra panoramas, conciertos, ferias, festivales, escapadas y actividades por categoria, fecha y ubicacion. Encuentra que hacer hoy cerca de ti."
        path="/explore"
      />
      {/* Header */}
      <div className={`border-b sticky top-14 md:top-16 z-40 ${isDark ? 'night-header' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 md:py-4">
          {/* Top row: Territory + Search + Filters button — single line on desktop */}
          <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-3">
            <TerritorySelector onComplete={handleTerritoryChange} showAllCommunes={true} />

            <div className="flex items-center gap-2 md:flex-1 md:min-w-0">
              <form onSubmit={handleSearch} className="flex-1 min-w-0">
                <div className="relative">
                  <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <Input
                    type="text"
                    placeholder={t('searchEvents')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`pl-12 pr-9 py-2.5 rounded-full border-0 focus:ring-2 focus:ring-[#00CC66] ${isDark ? 'bg-white/6 text-white placeholder:text-white/40' : 'bg-gray-50'}`}
                    data-testid="search-input"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => { setSearchTerm(''); setPage(1); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </form>

              <Button
                variant="outline"
                className="rounded-full gap-2 shrink-0"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="filter-toggle"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden xs:inline">{t('filters')}</span>
                {activeFiltersCount > 0 && (
                  <Badge className="bg-[#00CC66] text-white ml-1">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {/* Filters panel */}
          {showFilters && (
            <div className={`mt-4 p-4 rounded-2xl animate-fadeIn ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50'}`} data-testid="filters-panel">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                {/* Category — mobile only (desktop has visual rail) */}
                <div className="md:hidden">
                  <Select
                    value={categoryId || 'all'}
                    onValueChange={(v) => { setCategoryId(v === 'all' ? '' : v); setPage(1); setShowFilters(false); }}
                  >
                    <SelectTrigger className="rounded-xl w-full" data-testid="category-filter">
                      <SelectValue placeholder={t('category')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allCategories')}</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{translateText(cat.name)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date filter */}
                <Select value={dateFilter || 'all'} onValueChange={(v) => { setDateFilter(v === 'all' ? '' : v); setPage(1); setShowFilters(false); }}>
                  <SelectTrigger className="rounded-xl" data-testid="date-filter">
                    <SelectValue placeholder="Fecha" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{translateText('Cualquier fecha')}</SelectItem>
                    {dateFilters.map((df) => (
                      <SelectItem key={df.value} value={df.value}>{df.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Price */}
                <Select 
                  value={isFree === null ? 'all' : isFree.toString()} 
                  onValueChange={(v) => { setIsFree(v === 'all' ? null : v === 'true'); setPage(1); setShowFilters(false); }}
                >
                  <SelectTrigger className="rounded-xl" data-testid="price-filter">
                    <SelectValue placeholder={t('price')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{translateText('Cualquier precio')}</SelectItem>
                    <SelectItem value="true">{t('free')}</SelectItem>
                    <SelectItem value="false">{t('paid')}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Audience */}
                <Select value={audience || 'all'} onValueChange={(v) => { setAudience(v === 'all' ? '' : v); setPage(1); setShowFilters(false); }}>
                  <SelectTrigger className="rounded-xl" data-testid="audience-filter">
                    <SelectValue placeholder={t('audience')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allAudiences')}</SelectItem>
                    {audiences.map((a) => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear filters */}
              {activeFiltersCount > 0 && (
                <div className="mt-4 flex justify-end">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t('clearFilters')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Category rail (desktop only — mobile uses filters dropdown) */}
        {categories.length > 0 && (
          <div className="hidden md:block mb-8">
            <CategoryRail
              categories={categories}
              selectedCategoryId={categoryId}
              onSelect={(v) => { setCategoryId(v); setPage(1); }}
            />
          </div>
        )}

        {/* Results header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#064E3B]">
              {t('allEvents')}
            </h1>
            <p className="text-gray-500">
              {total} {t('eventsFound')}
              {selectedCommune && ` ${translateText('en')} ${selectedCommune.name}`}
              {!selectedCommune && selectedRegion && ` ${translateText('en')} ${selectedRegion.name}`}
            </p>
          </div>
        </div>

        {/* Events grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-3xl overflow-hidden bg-white">
                <Skeleton className="h-48 w-full" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event, index) => (
                <div 
                  key={event.id}
                  className="animate-fadeIn"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <EventCard event={event} />
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-full w-10 h-10 ${
                          page === pageNum ? 'bg-[#00CC66] text-white' : ''
                        }`}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-3xl">
            <CalendarIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {t('noEvents')}
            </h3>
            <p className="text-gray-400 mb-6">
              {t('tryAdjustingFilters')}
            </p>
            <Button onClick={clearFilters} className="btn-primary">
              {t('clearFilters')}
            </Button>
          </div>
        )}
      </div>
      <SeoLinks />
      <Footer />
    </div>
  );
};
